import jsPDF from 'jspdf';

const BRAND_BLUE = [29, 44, 95];
const PAGE_W = 210;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE_H = 6.5;

// ── header ────────────────────────────────────────────────────────────────────
function addHeader(doc, noteType, clientName, date) {
  doc.setFillColor(...BRAND_BLUE);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Avenues Unlimited Counseling Center', PAGE_W / 2, 14, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(noteType, MARGIN, 33);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(`Client: ${clientName || '—'}`, MARGIN, 41);
  doc.text(`Date: ${date || '—'}`, MARGIN, 47);

  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, 51, PAGE_W - MARGIN, 51);
  return 58;
}

// ── section heading ───────────────────────────────────────────────────────────
function sectionHead(doc, title, y) {
  if (y > 270) { doc.addPage(); y = 20; }
  doc.setFillColor(237, 240, 247);
  doc.rect(MARGIN, y - 4, CONTENT_W, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND_BLUE);
  doc.text(title.toUpperCase(), MARGIN + 2, y + 1);
  doc.setTextColor(0, 0, 0);
  return y + 9;
}

// ── field block ───────────────────────────────────────────────────────────────
function field(doc, label, value, y) {
  if (y > 272) { doc.addPage(); y = 20; }
  const text = value && String(value).trim() ? String(value).trim() : '—';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(label, MARGIN, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(text, CONTENT_W);
  lines.forEach(ln => {
    if (y > 278) { doc.addPage(); y = 20; }
    doc.text(ln, MARGIN, y);
    y += LINE_H;
  });
  return y + 3;
}

// ── page footers ──────────────────────────────────────────────────────────────
function addFooters(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      `Avenues Unlimited Counseling Center  •  Confidential  •  Page ${i} of ${total}`,
      PAGE_W / 2, 290, { align: 'center' }
    );
  }
}

// ── note-type formatters ──────────────────────────────────────────────────────
function fmtSOAP(doc, p, y) {
  y = sectionHead(doc, 'Subjective', y);
  y = field(doc, 'Mood', p.subjective?.mood, y);
  y = field(doc, 'Feelings Expressed', p.subjective?.feelingsExpressed, y);
  y = field(doc, 'Session Focus', p.subjective?.sessionFocus, y);

  y = sectionHead(doc, 'Objective', y);
  y = field(doc, 'Interventions Used', p.objective?.interventions, y);
  y = field(doc, 'Additional Techniques', p.objective?.additionalTechniques, y);
  y = field(doc, 'Engagement Level', p.objective?.engagementLevel, y);

  y = sectionHead(doc, 'Assessment', y);
  y = field(doc, 'Client Response', p.assessment?.clientResponse, y);
  y = field(doc, 'Client Progress', p.assessment?.clientProgress, y);

  y = sectionHead(doc, 'Plan', y);
  y = field(doc, 'Plan for Future Sessions', p.plan, y);

  if (p.sessionType) {
    y = sectionHead(doc, 'Session Info', y);
    y = field(doc, 'Session Type', p.sessionType, y);
  }
  return y;
}

const TA_QUESTIONS = [
  ['endVision',       'End Vision — How are you different at end of therapy?'],
  ['currentState',    'Currently'],
  ['desiredState',    'How you would like to be'],
  ['feelings',        'What I am feeling'],
  ['selfBeliefs',     'Beliefs about myself'],
  ['otherBeliefs',    'Beliefs about others'],
  ['actions',         'What I am doing'],
  ['enhanceLife',     'What could you change to enhance your life?'],
  ['howToAccomplish', 'What would you need to do to accomplish that?'],
  ['benefit',         'What about that would be beneficial or pleasing to you?'],
  ['measureSuccess',  'How would we both know you\'ve gotten there?'],
  ['sabotage',        'How could you sabotage your success?'],
  ['avoidSabotage',   'What will you do instead to ensure success?'],
  ['loss',            'Loss'],
  ['gain',            'Gain'],
];

function fmtTA(doc, p, y) {
  y = sectionHead(doc, 'TA Contract Responses', y);
  TA_QUESTIONS.forEach(([key, label]) => {
    if (p[key]) y = field(doc, label, p[key], y);
  });
  return y;
}

const INTAKE_FIELDS = [
  ['presentingProblems',              'Presenting Problems'],
  ['familyMedicalHistory',            'Family Medical History'],
  ['traumaHistory',                   'Trauma History'],
  ['traumaHistoryOther',              'Trauma History (Other)'],
  ['substanceAbuseStatus',            'Substance Abuse Status'],
  ['substanceAbuseFamilyHistory',     'Substance Abuse Family History'],
  ['substanceAbuseFamilyHistoryOther','Substance Abuse Family History (Other)'],
  ['previousTherapyExperiences',      'Previous Therapy Experiences'],
  ['previousTherapyNotes',            'Previous Therapy Notes'],
  ['selfHarmHistory',                 'Self-Harm History'],
  ['relationshipStatus',              'Relationship Status'],
  ['relationshipSatisfaction',        'Relationship Satisfaction'],
  ['protectiveFactors',               'Protective Factors / Strengths'],
  ['copingSkills',                    'Coping Skills'],
  ['mentalStatusExam',                'Mental Status Exam'],
];

function fmtIntake(doc, p, y) {
  INTAKE_FIELDS.forEach(([key, label]) => {
    const val = Array.isArray(p[key]) ? p[key].join(', ') : p[key];
    if (val) y = field(doc, label, val, y);
  });
  return y;
}

function fmtCollateral(doc, p, y) {
  y = sectionHead(doc, 'Contact Details', y);
  y = field(doc, 'Contact Type', p.contactType, y);
  y = field(doc, 'Contact With', p.contactWith, y);
  y = field(doc, 'Duration', p.contactDuration, y);
  y = field(doc, 'Permission to Contact', p.permissionToContact ? 'Yes' : 'No', y);

  y = sectionHead(doc, 'Content', y);
  y = field(doc, 'Purpose', p.contactPurpose, y);
  y = field(doc, 'Summary', p.contactSummary, y);
  y = field(doc, 'Follow-Up', p.followUp, y);
  return y;
}

// ── school note letter ────────────────────────────────────────────────────────
// School notes go out as the same letterhead letter the Preview / Print window
// shows in SchoolNoteComponents.jsx (not the generic field dump) — keep the
// wording and layout here in sync with that preview.
const LETTER_W = 215.9;
const LETTER_H = 279.4;
const LETTER_MARGIN = 22;
const LETTER_CONTENT_W = LETTER_W - LETTER_MARGIN * 2;
const LETTER_LINE_H = 5.6;
const LETTER_BOTTOM = LETTER_H - 28; // keep text clear of the footer image

// YYYY-MM-DD (or ISO timestamp) → MM/DD/YYYY, matching the preview's formatDate
function fmtLetterDate(dateStr) {
  if (!dateStr) return '';
  const parts = String(dateStr).split('T')[0].split('-');
  return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : String(dateStr);
}

function letterDayOfWeek(dateStr) {
  const parts = String(dateStr || '').split('T')[0].split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[new Date(parts[0], parts[1] - 1, parts[2]).getDay()];
}

async function loadPublicImage(path) {
  const res = await fetch(`${process.env.PUBLIC_URL || ''}${path}`);
  if (!res.ok) throw new Error(`Could not load ${path}`);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const size = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
  return { dataUrl, ...size };
}

async function buildSchoolNoteLetter(p, clientName) {
  const [letterhead, footer] = await Promise.all([
    loadPublicImage('/letterhead.png'),
    loadPublicImage('/footer.png'),
  ]);

  const doc = new jsPDF({ format: 'letter' });
  const name = p.clientName || clientName || '';

  // Letterhead, centered at the top of the first page
  const headW = 72;
  const headH = headW * (letterhead.h / letterhead.w);
  doc.addImage(letterhead.dataUrl, 'PNG', (LETTER_W - headW) / 2, 10, headW, headH);
  let y = 10 + headH + 12;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  const para = (text, gapAfter = 5) => {
    String(text).split('\n').forEach((block) => {
      if (!block.trim()) { y += LETTER_LINE_H; return; }
      doc.splitTextToSize(block, LETTER_CONTENT_W).forEach((ln) => {
        if (y > LETTER_BOTTOM) { doc.addPage(); y = 24; }
        doc.text(ln, LETTER_MARGIN, y);
        y += LETTER_LINE_H;
      });
    });
    y += gapAfter;
  };

  para(fmtLetterDate(p.date), 6);
  para(`Re: ${name}`, 1);
  para(`DOB: ${fmtLetterDate(p.clientDob)}`, 8);
  para('To whom it may concern,');

  const dayOfWeek = p.appointmentDayOfWeek || letterDayOfWeek(p.appointmentDate);
  const content = p.customContent
    ? (p.customNoteContent || '')
    : `This letter is to document ${name}'s presence at Avenues Unlimited Counseling Center for ${p.appointmentTime ? `their ${p.appointmentTime}` : 'their'} session on ${dayOfWeek} ${fmtLetterDate(p.appointmentDate)}.${p.additionalNotes ? `\n\n${p.additionalNotes}` : ''}`;
  para(content);

  para('If further information is needed, please contact me directly using the information below.', 8);

  // Keep the closing + signature block together on one page
  if (y > LETTER_BOTTOM - 55) { doc.addPage(); y = 24; }
  para('Sincerely,', 3);

  if (p.signatureData && String(p.signatureData).startsWith('data:image')) {
    try {
      const sigW = 53;
      const props = doc.getImageProperties(p.signatureData);
      const sigH = Math.min(24, sigW * (props.height / props.width));
      doc.addImage(p.signatureData, 'PNG', LETTER_MARGIN, y, sigW, sigH);
      y += sigH + 6;
    } catch (e) { /* skip bad image */ }
  }

  para(`${p.therapistName || ''}${p.therapistCredentials ? `, ${p.therapistCredentials}` : ''}`, 1);
  para(p.therapistTitle || 'Therapist', 2);
  doc.setFontSize(10);
  para(`p. ${p.therapistPhone || ''}${p.therapistPhoneExtension ? ` ext. ${p.therapistPhoneExtension}` : ''}`, 0);
  para(`e. ${p.therapistEmail || ''}`, 0);

  // Footer strip at the bottom of every page
  const footW = LETTER_CONTENT_W;
  const footH = footW * (footer.h / footer.w);
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.addImage(footer.dataUrl, 'PNG', LETTER_MARGIN, LETTER_H - 12 - footH, footW, footH);
  }

  return doc;
}

function fmtHandwritten(doc, p, y) {
  if (p.title) y = field(doc, 'Title', p.title, y);
  if (p.drawingData) {
    try {
      if (y > 200) { doc.addPage(); y = 20; }
      const imgH = Math.min(120, 270 - y);
      doc.addImage(p.drawingData, 'PNG', MARGIN, y, CONTENT_W, imgH);
      y += imgH + 6;
    } catch (_) {
      y = field(doc, 'Note', 'Handwritten image could not be rendered.', y);
    }
  }
  return y;
}

// ── ROI formatter ─────────────────────────────────────────────────────────────
function fmtROI(doc, s, y) {
  const gs = (camel, snake) => s[camel] || s[snake] || '';
  const gv = (camel, snake) => {
    const v = s[camel] !== undefined && s[camel] !== null ? s[camel] : s[snake];
    return v === true || v === 'true' || v === 1 || v === '1' || v === 't';
  };

  // Single-line checkbox row: "[X] label" / "[ ] label" (ASCII — jsPDF helvetica
  // can't render Unicode checkmarks reliably).
  const checkLine = (checked, label, yy) => {
    if (yy > 274) { doc.addPage(); yy = 20; }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(`${checked ? '[X]' : '[  ]'} ${label}`, CONTENT_W);
    lines.forEach((ln, i) => {
      if (yy > 280) { doc.addPage(); yy = 20; }
      doc.text(ln, MARGIN + (i === 0 ? 0 : 6), yy);
      yy += LINE_H;
    });
    return yy + 1;
  };

  const sigImage = (label, dataUrl, yy) => {
    if (!dataUrl || !String(dataUrl).startsWith('data:image')) return yy;
    if (yy > 235) { doc.addPage(); yy = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text(label, MARGIN, yy); yy += 4;
    try { doc.addImage(dataUrl, 'PNG', MARGIN, yy, 70, 26); } catch (e) { /* skip bad image */ }
    return yy + 30;
  };

  y = sectionHead(doc, 'Client Information', y);
  y = field(doc, 'Client Name', gs('clientName', 'client_name'), y);
  y = field(doc, 'Date of Birth', gs('clientDob', 'client_dob'), y);
  y = field(doc, 'Address', gs('clientAddress', 'client_address'), y);
  y = field(doc, 'Phone', gs('clientPhone', 'client_phone'), y);

  y = sectionHead(doc, 'Release Information To', y);
  y = field(doc, 'Name', gs('authorizeReleaseTo', 'authorize_release_to'), y);
  y = field(doc, 'Relationship', gs('releaseToRelationship', 'release_to_relationship'), y);
  y = field(doc, 'Phone / Email', gs('releaseToPhone', 'release_to_phone'), y);

  y = sectionHead(doc, 'Authorized Information Release', y);
  const release = [
    ['shareProgressWithProvider', 'share_progress_with_provider', 'Share progress and treatment details with my primary care provider or psychiatrist'],
    ['coordinateCareWithPsychiatrist', 'coordinate_care_with_psychiatrist', 'Coordinate care with my psychiatrist or prescribing provider'],
    ['shareWithSchool', 'share_with_school', "Share treatment information with my child's school"],
    ['speakWithFamily', 'speak_with_family', 'Speak with a family member, partner, or support person'],
    ['billUsingOtherCard', 'bill_using_other_card', 'Bill services using a card not in my name'],
    ['provideDocumentationToProvider', 'provide_documentation_to_provider', 'Provide documentation to another mental health provider'],
    ['allowEmergencyContactInform', 'allow_emergency_contact_inform', 'Allow emergency contacts to be informed in case of a crisis'],
    ['schedulingInfoOnly', 'scheduling_info_only', 'Scheduling or appointment information only'],
    ['treatmentProgress', 'treatment_progress', 'Treatment progress and participation'],
    ['diagnosisAndPlan', 'diagnosis_and_plan', 'Diagnosis and treatment plan'],
    ['billingAndPayment', 'billing_and_payment', 'Billing and payment details'],
    ['fullClinicalRecords', 'full_clinical_records', 'Full clinical records'],
  ];
  release.forEach(([c, sn, label]) => { y = checkLine(gv(c, sn), label, y); });
  const releaseOther = gs('releaseOtherSpecify', 'release_other_specify');
  if (releaseOther) y = checkLine(true, `Other: ${releaseOther}`, y);

  y = sectionHead(doc, 'Purpose of Disclosure', y);
  const purposes = [
    ['purposeCoordinationCare', 'purpose_coordination_care', 'Coordination of care'],
    ['purposeBilling', 'purpose_billing', 'Billing'],
    ['purposeEmergency', 'purpose_emergency', 'Emergency or safety planning'],
    ['purposeSupportFromFamily', 'purpose_support_from_family', 'Support from family/friends'],
  ];
  purposes.forEach(([c, sn, label]) => { y = checkLine(gv(c, sn), label, y); });
  const purposeOther = gs('purposeOtherSpecify', 'purpose_other_specify');
  if (purposeOther) y = checkLine(true, `Other: ${purposeOther}`, y);

  y = sectionHead(doc, 'Duration', y);
  let durationText = '—';
  if (gv('durationOneYear', 'duration_one_year')) durationText = 'One year from today';
  else if (gv('durationEndOfTreatment', 'duration_end_of_treatment')) durationText = 'End of treatment';
  else if (gv('durationSpecificDate', 'duration_specific_date')) durationText = `Until: ${gs('specificExpirationDate', 'specific_expiration_date')}`;
  y = field(doc, 'Duration', durationText, y);

  if (gv('eapAuthorizeSharing', 'eap_authorize_sharing') || gs('eapEmployer', 'eap_employer')) {
    y = sectionHead(doc, 'Employee Assistance Program (EAP)', y);
    const eapEmployer = gs('eapEmployer', 'eap_employer');
    if (eapEmployer) y = field(doc, 'Employer', eapEmployer, y);
    y = checkLine(gv('eapAuthorizeSharing', 'eap_authorize_sharing'), 'Authorized to share session dates and billing information with employer', y);
    y = checkLine(gv('eapUnderstandNoDetails', 'eap_understand_no_details'), 'Understands no clinical details will be shared unless explicitly authorized', y);
    y = checkLine(gv('eapUnderstandRefusalImpact', 'eap_understand_refusal_impact'), 'Understands refusal may impact ability to use EAP services', y);
  }

  y = sectionHead(doc, 'Authorization Details', y);
  y = field(doc, 'Submitted', s.created_at ? new Date(s.created_at).toLocaleString() : '', y);
  y = field(doc, 'Client Signed', gs('clientSignatureDate', 'client_signature_date'), y);
  const guardianName = gs('guardianName', 'guardian_name');
  if (guardianName) {
    y = field(doc, 'Guardian', guardianName, y);
    y = field(doc, 'Guardian Signed', gs('guardianSignatureDate', 'guardian_signature_date'), y);
  }
  const witnessName = gs('witnessName', 'witness_name');
  if (witnessName) {
    y = field(doc, 'Witness', witnessName, y);
    y = field(doc, 'Witness Signed', gs('witnessSignatureDate', 'witness_signature_date'), y);
  }

  y = sectionHead(doc, 'Signatures', y);
  y = sigImage('Client Signature', gs('clientSignatureData', 'client_signature_data'), y);
  y = sigImage('Guardian Signature', gs('guardianSignatureData', 'guardian_signature_data'), y);
  y = sigImage('Witness Signature', gs('witnessSignatureData', 'witness_signature_data'), y);

  return y;
}

// ── public API ────────────────────────────────────────────────────────────────
// Async because the school note letter loads the letterhead/footer images.
export async function generateNotePDF(note, clientName) {
  let p = {};
  try { p = typeof note.notes === 'string' ? JSON.parse(note.notes) : (note.notes || {}); } catch {}

  const noteType = p.type || 'SOAP Note';
  const date = p.date || new Date(note.created_at).toLocaleDateString();

  if (p.type === 'SchoolNote') {
    const letter = await buildSchoolNoteLetter(p, clientName);
    const safeLetterName = (clientName || 'Client').replace(/[,\s]+/g, '_');
    return {
      pdfBase64: letter.output('datauristring').split(',')[1],
      fileName:  `School_Note_${safeLetterName}_${date.replace(/\//g, '-')}.pdf`,
      noteType:  'School Note',
      date,
    };
  }

  const doc = new jsPDF();
  let y = addHeader(doc, noteType, clientName, date);

  if      (!p.type || p.type === 'SOAP Note')        y = fmtSOAP(doc, p, y);
  else if (p.type === 'TA Contract')                 y = fmtTA(doc, p, y);
  else if (p.type === 'IntakeAssessment')            y = fmtIntake(doc, p, y);
  else if (p.type === 'CollateralContact')           y = fmtCollateral(doc, p, y);
  else if (p.type === 'HandwrittenNote')             y = fmtHandwritten(doc, p, y);

  addFooters(doc);

  const safeDate  = date.replace(/\//g, '-');
  const safeName  = (clientName || 'Client').replace(/[,\s]+/g, '_');
  const safeType  = noteType.replace(/\s+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `${safeType}_${safeName}_${safeDate}.pdf`,
    noteType,
    date,
  };
}

export function generateROIPDF(submission, clientName) {
  const doc = new jsPDF();
  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  let y = addHeader(doc, 'Release of Information', clientName, date);
  y = fmtROI(doc, submission, y);
  addFooters(doc);

  const safeName = (clientName || 'Client').replace(/[,\s]+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `ROI_${safeName}_${date.replace(/\//g, '-')}.pdf`,
    noteType:  'Release of Information',
    date,
  };
}
